import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { decode } from 'he';
import {
  ServerMessage,
  ClientMessage,
  GameState,
  PlayerProgress,
  LeaderboardEntry,
  TOTAL_QUESTIONS,
  MAX_POSSIBLE_SCORE,
} from '@shared/protocol';
import { getWsUrl } from '../config';

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

// Format time as mm:ss
function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

// Format time with milliseconds
function formatTimeMs(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const milliseconds = ms % 1000;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
}

function LobbyPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting');
  const [error, setError] = useState<string | null>(null);
  
  // Game state
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [progress, setProgress] = useState<PlayerProgress[]>([]);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [finishedPlayers, setFinishedPlayers] = useState<LeaderboardEntry[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestionText, setCurrentQuestionText] = useState('');
  const [allAnswered, setAllAnswered] = useState(false);
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleMessageRef = useRef<(message: ServerMessage) => void>(() => {});

  // Build the join URL for players
  const joinUrl = sessionId ? `${window.location.origin}/play/${sessionId}` : '';

  const send = useCallback((message: ClientMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  const connect = useCallback(() => {
    if (!sessionId) return;
    
    setConnectionStatus('connecting');
    
    const wsUrl = getWsUrl(`/api/session/${sessionId}/ws`);
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    
    ws.onopen = () => {
      setConnectionStatus('connected');
      setError(null);
      // Send host_hello to identify as host viewer
      ws.send(JSON.stringify({ type: 'host_hello' }));
    };
    
    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as ServerMessage;
        handleMessageRef.current(message);
      } catch (e) {
        console.error('Failed to parse message:', e);
      }
    };
    
    ws.onclose = () => {
      setConnectionStatus('disconnected');
      // Try to reconnect
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      reconnectRef.current = setTimeout(connect, 2000);
    };
    
    ws.onerror = () => {
      setConnectionStatus('error');
    };
  }, [sessionId]);

  const handleMessage = useCallback((message: ServerMessage) => {
    switch (message.type) {
      case 'state':
        setGameState(message.state);
        if (message.state.leaderboard) {
          setFinishedPlayers(message.state.leaderboard);
        }
        // Reset all game states when returning to lobby (after continue)
        if (message.state.status === 'lobby') {
          setProgress([]);
          setElapsedMs(0);
          setCurrentQuestionIndex(0);
          setCurrentQuestionText('');
          setAllAnswered(false);
          setFinishedPlayers([]);
        }
        break;
        
      case 'player_update':
        setGameState(prev => prev ? { ...prev, players: message.players } : null);
        break;
        
      case 'game_started':
        setGameState(prev => prev ? { ...prev, status: 'running', startedAt: message.startedAt } : null);
        setFinishedPlayers([]);
        setProgress([]);
        setCurrentQuestionIndex(0);
        break;
        
      case 'progress_update':
        setProgress(message.progress);
        setElapsedMs(message.elapsedMs);
        setCurrentQuestionIndex(message.currentQuestionIndex);
        setCurrentQuestionText(message.currentQuestionText);
        setAllAnswered(message.allAnswered);
        break;
        
      case 'game_finished':
        setFinishedPlayers(message.leaderboard);
        setGameState(prev => prev ? { ...prev, status: 'finished' } : null);
        break;
        
      case 'error':
        setError(message.message);
        setTimeout(() => setError(null), 3000);
        break;
        
      case 'pong':
        break;
    }
  }, []);

  // Keep the ref updated with latest handler
  useEffect(() => {
    handleMessageRef.current = handleMessage;
  }, [handleMessage]);

  // Connect on mount
  useEffect(() => {
    connect();
    
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect]);
  
  // Ping interval
  useEffect(() => {
    if (connectionStatus !== 'connected') return;
    
    const pingInterval = setInterval(() => {
      send({ type: 'ping' });
    }, 30000);
    
    return () => clearInterval(pingInterval);
  }, [connectionStatus, send]);
  
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };
  
  const startNewGame = () => {
    if (wsRef.current) wsRef.current.close();
    if (reconnectRef.current) clearTimeout(reconnectRef.current);
    navigate('/');
  };

  // Loading / connecting state
  if (connectionStatus === 'connecting' && !gameState) {
    return (
      <div className="loading-page">
        <div className="waiting-spinner" />
        <p>Connecting to session {sessionId}...</p>
      </div>
    );
  }

  // Game finished - show final leaderboard
  if (gameState?.status === 'finished' && finishedPlayers.length > 0) {
    return (
      <div className="page">
        <div className="container">
          <div className="results-header">
            <div className="trophy-icon">🏆</div>
            <h1>Game Over!</h1>
            <p className="session-code">Session: {sessionId}</p>
          </div>
          
          <h2>Final Results</h2>
          <ul className="leaderboard">
            {finishedPlayers.map((entry, index) => {
              const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}`;
              return (
                <li 
                  key={entry.playerId} 
                  className={`leaderboard-item ${index === 0 ? 'winner' : ''}`}
                >
                  <div className="leaderboard-rank">{medal}</div>
                  <div className="leaderboard-info">
                    <div className="leaderboard-name">{entry.nick}</div>
                    <div className="leaderboard-stats">
                      <span className="score">{entry.score} points</span>
                    </div>
                  </div>
                  <div className="leaderboard-time">
                    {formatTimeMs(entry.totalTimeMs)}
                  </div>
                </li>
              );
            })}
          </ul>
          
          {/* Host control notice */}
          <div className="host-notice mt-3">
            <p>🎮 The host player controls when to continue</p>
          </div>
          
          <button onClick={startNewGame} className="btn btn-secondary btn-full mt-3">
            🏠 New Game
          </button>
        </div>
      </div>
    );
  }

  // Live race view (game running)
  if (gameState?.status === 'running') {
    // Sort progress by score (highest first)
    const sortedProgress = [...progress].sort((a, b) => b.score - a.score);
    const answeredCount = progress.filter(p => p.hasAnsweredCurrent).length;
    const totalPlayers = progress.length;
    
    return (
      <div className="page">
        <div className="container">
          <div className="race-header">
            <div className="race-title">
              <span className="race-icon">🏁</span>
              <h1>Q{currentQuestionIndex + 1}/{TOTAL_QUESTIONS}</h1>
            </div>
            <div className="race-timer">
              ⏱️ {formatTime(elapsedMs)}
            </div>
          </div>
          
          {/* Current question display */}
          {currentQuestionText && (
            <div className="current-question-display mb-3">
              <p className="question-text">{decode(currentQuestionText)}</p>
              <div className="answer-status">
                {allAnswered ? (
                  <span className="all-answered">✓ All players answered!</span>
                ) : (
                  <span className="waiting-answers">{answeredCount}/{totalPlayers} answered</span>
                )}
              </div>
            </div>
          )}
          
          {/* Race track showing all players */}
          <div className="race-track">
            {sortedProgress.map((player, index) => {
              // Progress based on score relative to max possible
              const progressPercent = (player.score / MAX_POSSIBLE_SCORE) * 100;
              const isLeading = index === 0 && player.score > 0;
              
              return (
                <div key={player.playerId} className={`race-lane ${isLeading ? 'leading' : ''} ${player.hasAnsweredCurrent ? 'answered' : 'waiting'}`}>
                  <div className="race-player-info">
                    <span className="race-position">{index + 1}</span>
                    <span className="race-nick">{player.nick}</span>
                    <span className="race-score">{player.score} pts</span>
                    {player.hasAnsweredCurrent && <span className="answered-check">✓</span>}
                  </div>
                  <div className="race-progress-track">
                    <div 
                      className="race-progress-bar"
                      style={{ width: `${Math.max(5, progressPercent)}%` }}
                    >
                      <span className="race-runner">{player.hasAnsweredCurrent ? '🏃' : '⏳'}</span>
                    </div>
                  </div>
                  <div className="race-question">
                    {player.correctAnswers || 0}✓ / {player.currentQuestion}
                  </div>
                </div>
              );
            })}
          </div>
          
          {progress.length === 0 && (
            <div className="waiting-container">
              <div className="waiting-spinner" />
              <p>Waiting for players to answer...</p>
            </div>
          )}
          
          <div className="qr-mini mt-3">
            <p className="text-muted mb-2">Session: {sessionId}</p>
          </div>
        </div>
      </div>
    );
  }

  // Lobby view with QR code
  const playerCount = gameState?.players?.length || 0;
  const isConnected = connectionStatus === 'connected';
  const isReconnecting = connectionStatus === 'disconnected' || connectionStatus === 'connecting';
  
  return (
    <div className="page">
      <div className="container text-center">
        <div className="header mb-3">
          <div className="logo">📱</div>
          <h1>Scan to Join!</h1>
          <p className="session-code">
            Session Code: <strong>{sessionId}</strong>
          </p>
        </div>
        
        {error && (
          <div className="error-message mb-2">
            {error}
          </div>
        )}
        
        <div className="qr-container mb-3">
          <QRCodeSVG 
            value={joinUrl} 
            size={280}
            level="H"
            includeMargin={true}
          />
        </div>
        
        <button 
          className="btn btn-secondary mb-3"
          onClick={copyLink}
        >
          📋 Copy Link
        </button>
        
        <div className="player-count mb-3">
          <div className="player-count-number">{playerCount}</div>
          <div className="player-count-label">
            player{playerCount === 1 ? '' : 's'} joined
          </div>
          {playerCount > 0 && gameState?.players && (
            <div className="player-names">
              {gameState.players.map(p => p.nick).join(', ')}
            </div>
          )}
        </div>
        
        {isReconnecting && (
          <div className="reconnecting-notice mb-2">
            <span className="waiting-spinner small" />
            Reconnecting...
          </div>
        )}
        
        {isConnected && playerCount === 0 && (
          <div className="waiting-notice mb-2">
            <span className="pulse-dot" />
            Waiting for players to join...
          </div>
        )}
        
        {isConnected && playerCount > 0 && (
          <div className="ready-notice mb-2">
            ✓ Ready! First player can start the game.
          </div>
        )}
        
        <button onClick={startNewGame} className="btn btn-secondary btn-full mt-3">
          ✕ Cancel Game
        </button>
      </div>
    </div>
  );
}

export default LobbyPage;
